import React, { useMemo, Fragment, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Text from 'containers/MyHealth/components/Text'
import Divider from 'containers/MyHealth/components/Divider'
import _ from 'lodash'
import { formatDateTime } from '@enlyt/helpers/parse'
import { getUniqueValuesFromArray } from '@enlyt/helpers/getUniqueValuesFromArray'
import localization from './localization'
import themeStyles from '../styles'
import { useTheme } from 'contexts/ThemeProvider'

import useMediaQuery from 'hooks/useMediaQuery'
import RangeColorScale from 'components/RangeColorScale'
import HistoryResults from '../HistoryResults'
import FhirEnhancedActions from '@web/stores/FhirEnhanced/Actions'
import {
  parseHistoryTestResults,
  parseHealthLibrary
} from 'helpers/parseEnhanced'
import { getCurPatientId } from '@web/stores/Account/Select'
import {
  observationHistory,
  observationHistoryLoading,
  healthLibrary,
  healthLibraryLoading,
  observationHistoryError
} from 'stores/FhirEnhanced/Select'
import LearnMoreButton from '@web/components/HealthLibraryLearnMore'
import withLocalization from '@web/helpers/localizationUtils'
import { useTranslation } from 'react-i18next'
import { HEALTH_LIBRARY_MAPPING } from '@web/helpers/sources'

export const isNotInRange = testResultItem => {
  if (!testResultItem || !testResultItem.range) {
    return false
  }

  try {
    const { result, range, units } = testResultItem
    const normalizedRange = _.trim(_.replace(range, units, ''))
    const [min, max] = _.split(normalizedRange, ' - ').map(_.toNumber)

    return !(result >= min && result <= max)
  } catch {
    return false
  }
}

const DetailsResults = ({ test, testDates, t }) => {
  const patientId = useSelector(getCurPatientId)
  const dispatch = useDispatch()
  const { theme } = useTheme()
  const styles = themeStyles(theme)
  const { isMobile } = useMediaQuery()
  const getLastTestDate = dates => {
    const lastTestDate = new Date(
      Math.max(...dates.map(date => new Date(date)))
    )
    return formatDateTime(lastTestDate)
  }

  const sortByCode = results => {
    return _.orderBy(results, ['code'], ['asc'])
  }

  const history = useSelector(observationHistory)
  const historyLoading = useSelector(observationHistoryLoading)
  const historyError = useSelector(observationHistoryError)
  const [historyData, setHistoryData] = useState([])
  const [title, setTitle] = useState('')
  const [currentCode, setCurrentCode] = useState('')

  const healthArticles = useSelector(healthLibrary)
  const [healthArticleData, setHealthArticleData] = useState([])
  const [currentArticleCode, setCurrentArticleCode] = useState('')
  const healthArticlesLoading = useSelector(healthLibraryLoading)
  const { i18n } = useTranslation()
  const language = i18n.language === 'es' ? 'es' : 'en'

  const lastTestsForDetailsGroup = useMemo(
    () => {
      let results = test[getLastTestDate(testDates)]
      results = results.filter(item => {
        return item.result !== null && item.result !== ''
      })
      return getUniqueValuesFromArray(sortByCode(results))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [test]
  )

  useEffect(() => {
    if (!historyLoading?.[currentCode]) {
      const testData = _.get(history, currentCode, [])
      const parsedData = parseHistoryTestResults(testData)
      setHistoryData(parsedData)
    }
  }, [historyLoading, currentCode, history])

  useEffect(() => {
    if (!healthArticlesLoading?.[currentArticleCode]) {
      const healthArticleData = _.get(
        healthArticles?.[language],
        currentArticleCode,
        []
      )
      const parsedData = parseHealthLibrary(healthArticleData)
      setHealthArticleData(parsedData)
    }
  }, [healthArticlesLoading, currentArticleCode, healthArticles, language])

  if (!lastTestsForDetailsGroup.length) {
    return <p style={styles.noResults}>{t('noResults')}</p>
  }

  const isEmptyRange = value => _.isNil(value) || value === ''
  const getRange = item => {
    if (item?.textRange) {
      return item?.textRange
    }
    if (item?.range && !isEmptyRange(item?.low) && !isEmptyRange(item?.high)) {
      return item?.range
    }
    return null
  }

  const needShowTrendLine = item => {
    return (
      !isNaN(item?.result) &&
      item?.range &&
      !isEmptyRange(item?.low) &&
      !isEmptyRange(item?.high) &&
      item?.low !== item?.high
    )
  }

  const openResultHistory = (code, title) => {
    const testData = _.get(history, code, [])
    setTitle(title)
    setCurrentCode(code)
    if (!_.isEmpty(testData)) {
      const parsedData = parseHistoryTestResults(testData)
      setHistoryData(parsedData)
    } else {
      dispatch(FhirEnhancedActions.observationHistorySearch(patientId, code))
    }
  }

  const openLearnMorePopup = ({ code, terms }) => {
    const key = code || terms
    const healthArticleData = _.get(healthArticles?.[language], key, [])
    setCurrentArticleCode(key)
    if (!_.isEmpty(healthArticleData)) {
      const parsedData = parseHealthLibrary(healthArticleData)
      setHealthArticleData(parsedData)
    } else if (!healthArticlesLoading?.[key]) {
      dispatch(
        FhirEnhancedActions.healthLibrarySearch(
          language,
          code ? undefined : terms,
          code,
          HEALTH_LIBRARY_MAPPING.TESTS
        )
      )
    }
  }
  const handleCloseLearnMorePopup = () => {
    setHealthArticleData([])
  }

  return (
    <>
      {lastTestsForDetailsGroup.map((item, index) => (
        <Fragment key={index}>
          {item.result !== undefined &&
            item.result !== null &&
            item.result !== '' && (
              <div style={styles.textContainer}>
                <p style={styles.titleContainer}>
                  <strong style={{ whiteSpace: 'nowrap', ...styles.title }}>
                    {item.code}
                  </strong>
                  <LearnMoreButton
                    data={healthArticleData}
                    onClick={() =>
                      openLearnMorePopup({
                        code: item?.loincCode,
                        terms: item?.code
                      })
                    }
                    onClose={handleCloseLearnMorePopup}
                    shouldLoading={healthArticlesLoading?.[currentArticleCode]}
                    mapKey={item?.loincCode ? item?.loincCode : item?.code}
                    buttonStyle={styles.learnMoreButton}
                    language={language}
                  />
                </p>
                {item.effectiveDateTime && (
                  <Text
                    noPadding
                    text={t('date') + ': ' + item.effectiveDateTime}
                    style={styles.text}
                  />
                )}
                {getRange(item) && (
                  <Text
                    noPadding
                    text={t('standardRange') + ': ' + getRange(item)}
                    style={styles.text}
                  />
                )}
              </div>
            )}
          <div style={styles.barChartAre(isMobile)}>
            <RangeColorScale {...item} />
          </div>
          {needShowTrendLine(item) && item.codeHistory && (
            <HistoryResults
              data={historyData}
              title={title}
              loading={historyLoading?.[currentCode]}
              error={historyError?.[currentCode]}
              onClick={() => openResultHistory(item.codeHistory, item.code)}
              needShowTrendLine={needShowTrendLine(item)}
            />
          )}
          <Divider customStyle={styles.marginBig} />
        </Fragment>
      ))}
    </>
  )
}

export default withLocalization(DetailsResults, localization)
